/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Save, Plus, Trash2, Send, Eye, Loader2, Package, X, Search, Receipt } from "lucide-react";
import { toast } from "sonner";
import { adminApi, publicCatalogApi, catalogApi, formatApiError, api } from "@/lib/api";
import { lowerEmail } from "@/lib/text";
import { RentalPeriodNote, DatePickerField } from "@/components/location/DateTimeFields";

const EVENT_TYPES = ["Mariage", "Anniversaire", "Entreprise", "Séminaire", "Mairie", "Collectivité", "Photobooth", "Baptême", "Communion", "Association", "Senior", "Bar / Restaurant", "Location", "Autre"];
const PROFILE_KEYS = ["client_type", "raison_sociale", "nom_commercial", "siren", "siret", "tva_number", "tva_status", "contact_prenom", "contact_nom", "contact_fonction", "email_facturation"];
const EMPTY_PROFILE = Object.fromEntries(PROFILE_KEYS.map((k) => [k, ""]));
const profileOf = (src) => Object.fromEntries(PROFILE_KEYS.map((k) => [k, src?.[k] || ""]));
const isPro = (f) => f.client_type === "professionnel" || f.client_type === "association";
const STATUS_DEVIS = ["brouillon", "envoye", "accepte", "refuse", "expire", "signe", "acompte_recu"];
const STATUS_FACT = ["brouillon", "envoyee", "payee", "partielle", "impayee", "annulee"];

const CONTRACT_TEMPLATES = [
  ["", "Aucun contrat joint"],
  ["mariage", "Mariage"],
  ["anniversaire", "Anniversaire"],
  ["bapteme", "Baptême / Communion"],
  ["entreprise", "Soirée d'entreprise"],
  ["mairie", "Mairie / Collectivité"],
  ["association", "Soirée associative"],
  ["senior", "Animation senior"],
  ["photobooth", "Photobooth"],
  ["bar-restaurant", "DJ Set Bar & Restaurant"],
  ["location", "Contrat de location de matériel"],
  ["autre", "Prestation événementielle (générique)"],
];

const EVENT_TO_TPL = {
  Mariage: "mariage", Anniversaire: "anniversaire", "Baptême": "bapteme",
  Communion: "bapteme", Entreprise: "entreprise", "Séminaire": "entreprise",
  Mairie: "mairie", "Collectivité": "mairie",
  Association: "association", Senior: "senior", Photobooth: "photobooth", "Bar / Restaurant": "bar-restaurant", Location: "location", Autre: "autre",
};

const EMPTY = {
  doc_type: "devis",
  client_name: "",
  client_email: "",
  client_phone: "",
  client_address: "",
  ...EMPTY_PROFILE,
  event_date: "",
  event_type: "Mariage",
  event_location: "",
  event_time: "",
  payment_method: "Virement bancaire",
  lines: [{ description: "", quantity: 1, unit_price: 0, notes: "" }],
  notes: "",
  terms: "Devis valable 30 jours à compter de son émission.\nAcompte de 30 % à la signature pour confirmer définitivement la date.\nSolde réglable au plus tard 4 jours avant la prestation, par virement bancaire.",
  deposit_pct: 30,
  deposit_type: "percent",   // 'percent' | 'amount'
  deposit_amount: 0,
  discount_type: "none",     // 'none' | 'percent' | 'amount'
  discount_value: 0,
  valid_until: "",
  payment_due: "",
  contract_template: "mariage",
};

function fmtEur(v) {
  return Number(v || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
}

export default function AdminDevisEditor() {
  const { id } = useParams();
  const isNew = !id || id === "nouveau";
  const navigate = useNavigate();

  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [savedDoc, setSavedDoc] = useState(null);
  const [rental, setRental] = useState(null);
  const [showCatalog, setShowCatalog] = useState(false);
  const [catalog, setCatalog] = useState(null);
  const [catalogQuery, setCatalogQuery] = useState("");

  // Client picker (attach an existing client without re-typing)
  const [clients, setClients] = useState([]);
  const [pickerQuery, setPickerQuery] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    // Load client list once (used only when the user opens the picker)
    adminApi.users().then((all) => {
      setClients((all || []).filter((u) => u.role === "client"));
    }).catch(() => setClients([]));
  }, []);

  // Load default T&C for NEW devis so we always ship the latest CG
  useEffect(() => {
    if (!isNew) return;
    adminApi.billingDefaultTerms()
      .then((r) => { if (r?.terms) setForm((f) => ({ ...f, terms: r.terms })); })
      .catch(() => { /* keep hardcoded fallback */ });
  }, [isNew]);

  const fillFromClient = (u) => {
    const parts = [u.adresse, u.adresse_complement, [u.code_postal, u.ville].filter(Boolean).join(" ")].filter(Boolean);
    const composed = parts.join(", ");
    setForm((f) => ({
      ...f,
      ...profileOf(u),
      client_name: u.name || `${u.prenom || ""} ${u.nom || ""}`.trim() || f.client_name,
      client_email: (u.email || "").toLowerCase(),
      client_phone: u.phone || "",
      client_address: composed || f.client_address,
      event_type: u.type_evenement ? (EVENT_TYPES.find((t) => t.toLowerCase() === String(u.type_evenement).toLowerCase()) || f.event_type) : f.event_type,
    }));
    setPickerQuery("");
    setPickerOpen(false);
    toast.success(`Client rattaché : ${u.name || u.email}`);
  };

  const filteredClients = (() => {
    const q = pickerQuery.trim().toLowerCase();
    if (!q) return clients.slice(0, 8);
    return clients.filter((u) => {
      const hay = `${u.name || ""} ${u.email || ""} ${u.phone || ""} ${u.ville || ""} ${u.raison_sociale || ""} ${u.nom_commercial || ""}`.toLowerCase();
      return hay.includes(q);
    }).slice(0, 8);
  })();

  useEffect(() => {
    if (isNew) return;
    setLoading(true);
    // Load catalog in parallel to enrich lines with admin_only pricing metadata for legacy devis
    Promise.all([adminApi.getBilling(id), catalogApi.list().catch(() => [])])
      .then(([d, cat]) => {
        const adminOnly = (cat || []).filter((p) => p.admin_only);
        const enrichedLines = (d.lines && d.lines.length > 0) ? d.lines.map((l) => {
          if (l.is_admin_only) return l;
          const descLower = (l.description || "").toLowerCase();
          const match = adminOnly.find((p) => p.name && descLower.includes(p.name.toLowerCase()));
          if (match) {
            return {
              ...l,
              is_admin_only: true,
              prestataire_cost: l.prestataire_cost ?? (match.prestataire_cost ?? null),
              urssaf_rate: l.urssaf_rate ?? (match.urssaf_rate ?? 0.22),
              target_net_margin: l.target_net_margin ?? (match.target_net_margin ?? null),
            };
          }
          return l;
        }) : [{ description: "", quantity: 1, unit_price: 0, notes: "" }];
        setRental(d.rental_start_at || d.rental_id ? { id: d.rental_id, start: d.rental_start_at, end: d.rental_end_at, mode: d.rental_mode, number: d.rental_number } : null);
        setForm({
          doc_type: d.doc_type,
          client_name: d.client_name || "",
          client_email: d.client_email || "",
          client_phone: d.client_phone || "",
          client_address: d.client_address || "",
          ...profileOf(d),
          event_date: d.event_date || "",
          event_type: d.event_type || "Mariage",
          event_location: d.event_location || "",
          event_time: d.event_time || "",
          payment_method: d.payment_method || "Virement bancaire",
          lines: enrichedLines,
          notes: d.notes || "",
          terms: d.terms || "",
          deposit_pct: d.deposit_pct ?? 30,
          deposit_type: d.deposit_type ?? "percent",
          deposit_amount: d.deposit_amount ?? 0,
          discount_type: d.discount_type ?? "none",
          discount_value: d.discount_value ?? 0,
          valid_until: d.valid_until || "",
          payment_due: d.payment_due || "",
          status: d.status,
          contract_template: d.contract_template ?? "",
        });
        setSavedDoc(d);
      })
      .catch((e) => { toast.error(formatApiError(e)); navigate("/admin/devis"); })
      .finally(() => setLoading(false));
  }, [id, isNew, navigate]);

  const update = (k, v) => setForm((f) => {
    if (k === "event_type") {
      return { ...f, event_type: v, contract_template: EVENT_TO_TPL[v] ?? f.contract_template };
    }
    return { ...f, [k]: v };
  });
  const updateLine = (i, k, v) => setForm((f) => {
    const lines = [...f.lines];
    lines[i] = { ...lines[i], [k]: k === "quantity" || k === "unit_price" ? Number(v) || 0 : v };
    return { ...f, lines };
  });
  const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, { description: "", quantity: 1, unit_price: 0, notes: "" }] }));
  const removeLine = (i) => setForm((f) => ({ ...f, lines: f.lines.filter((_, j) => j !== i) }));
  const addLineFromCatalog = (pkg) => {
    const price = Number(pkg.price_ttc || 0);
    const hourly = pkg.unit === "heure";
    const desc = hourly ? pkg.name : pkg.name + (pkg.subtitle ? ` — ${pkg.subtitle}` : "");
    const isAdminOnly = !!pkg.admin_only;
    setForm((f) => ({
      ...f,
      ...(pkg.category === "bar-restaurant" ? { event_type: "Bar / Restaurant", contract_template: "bar-restaurant" } : {}),
      lines: [...f.lines.filter((l) => l.description.trim() || l.unit_price), {
        description: desc,
        quantity: hourly ? Number(pkg.min_quantity || 1) : 1,
        unit_price: price,
        unit: pkg.unit || "",
        min_quantity: pkg.min_quantity || null,
        notes: pkg.features && pkg.features.length ? pkg.features.slice(0, 6).join(" · ") : "",
        // Pre-fill margin metadata from the admin_only package (per-line editable)
        is_admin_only: isAdminOnly,
        prestataire_cost: isAdminOnly ? (pkg.prestataire_cost ?? null) : null,
        urssaf_rate: isAdminOnly ? (pkg.urssaf_rate ?? 0.22) : null,
        target_net_margin: isAdminOnly ? (pkg.target_net_margin ?? null) : null,
      }],
    }));
    toast.success(`Ligne ajoutée : ${pkg.name}`);
  };

  const subtotal = form.lines.reduce((s, l) => s + Number(l.quantity || 0) * Number(l.unit_price || 0), 0);
  const discountAmount = (() => {
    const t = form.discount_type || "none";
    const v = Number(form.discount_value) || 0;
    if (t === "none" || v <= 0) return 0;
    if (t === "percent") return Math.round(subtotal * Math.min(Math.max(v, 0), 100)) / 100;
    if (t === "amount") return Math.min(v, subtotal);
    return 0;
  })();
  const totalTtc = Math.max(0, subtotal - discountAmount);
  const deposit = (form.deposit_type === "amount")
    ? Math.min(Math.max(Number(form.deposit_amount) || 0, 0), totalTtc)
    : totalTtc * (Number(form.deposit_pct) || 0) / 100;

  const save = async () => {
    if (!form.client_name || form.client_name.trim().length < 2) {
      toast.error("Le nom du client est requis.");
      return;
    }
    if (form.lines.length === 0 || form.lines.every((l) => !l.description.trim())) {
      toast.error("Ajoutez au moins une ligne avec une description.");
      return;
    }
    const belowMin = form.lines.find((l) => l.min_quantity && Number(l.quantity) < Number(l.min_quantity));
    if (belowMin) {
      toast.error(`« ${belowMin.description} » : minimum ${belowMin.min_quantity} ${belowMin.unit === "heure" ? "heures" : ""}.`);
      return;
    }
    setSaving(true);
    try {
      let res;
      // Normalize empty optional fields to null so Pydantic EmailStr/Optional validators accept them
      const payload = {
        ...form,
        client_email: (form.client_email || "").trim() || null,
        client_phone: (form.client_phone || "").trim() || null,
        lines: form.lines.filter((l) => l.description.trim()),
      };
      if (isNew) {
        res = await adminApi.createBilling(payload);
        toast.success(`${res.doc_type === "devis" ? "Devis" : "Facture"} ${res.number} créé.`);
        navigate(`/admin/devis/${res.id}`, { replace: true });
      } else {
        res = await adminApi.updateBilling(id, payload);
        toast.success("Document mis à jour.");
      }
      setSavedDoc(res);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const sendDoc = async () => {
    if (!savedDoc) return;
    if (!savedDoc.client_email) {
      toast.error("Renseignez d'abord l'email du client.");
      return;
    }
    try {
      await adminApi.sendBilling(savedDoc.id);
      toast.success("Document envoyé au client par email.");
      const refreshed = await adminApi.getBilling(savedDoc.id);
      setSavedDoc(refreshed);
      setForm((f) => ({ ...f, status: refreshed.status }));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const openPdf = () => {
    if (!savedDoc) return;
    const token = localStorage.getItem("ma_token");
    fetch(adminApi.billingPdfUrl(savedDoc.id), { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      })
      .catch(() => toast.error("Impossible de récupérer le PDF."));
  };

  // ============ Aperçu PDF live (sans enregistrer) ============
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");

  const openPreview = async () => {
    if (previewLoading) return;
    // Minimal validation before hitting the API
    if (!form.client_name || form.client_name.trim().length < 2) {
      toast.error("Renseignez au moins le nom du client pour prévisualiser.");
      return;
    }
    setPreviewLoading(true);
    setPreviewOpen(true);
    // Free any older preview URL
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl("");
    }
    try {
      const payload = {
        ...form,
        client_email: (form.client_email || "").trim() || null,
        client_phone: (form.client_phone || "").trim() || null,
        lines: form.lines.filter((l) => l.description.trim()),
      };
      const blob = await adminApi.previewBilling(payload);
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
    } catch (e) {
      toast.error(formatApiError(e));
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    setPreviewOpen(false);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl("");
    }
  };

  // Free the blob URL when the component unmounts (avoid memory leaks)
  useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  // ============ Générer facture de solde manuellement ============
  const [soldeModalOpen, setSoldeModalOpen] = useState(false);
  const [soldeSubmitting, setSoldeSubmitting] = useState(false);
  const [soldeForm, setSoldeForm] = useState({
    paid_at: "",
    payment_method: "Virement bancaire",
    mark_paid: false,
  });

  const submitGenerateSolde = async () => {
    if (soldeSubmitting) return;
    setSoldeSubmitting(true);
    try {
      const body = {
        payment_method: soldeForm.payment_method || "Virement bancaire",
        mark_paid: !!soldeForm.mark_paid,
        paid_at: soldeForm.paid_at || undefined,
      };
      if (body.mark_paid && !body.paid_at) {
        toast.error("Renseignez la date de paiement pour marquer la facture payée.");
        setSoldeSubmitting(false);
        return;
      }
      const res = await adminApi.generateBalanceInvoice(savedDoc.id, body);
      toast.success(`Facture de solde ${res.balance_invoice?.number || ""} générée`);
      // Reload the devis to pick up balance_invoice_id/number
      const reloaded = await adminApi.getBilling(savedDoc.id);
      setSavedDoc(reloaded);
      setSoldeModalOpen(false);
      setSoldeForm({ paid_at: "", payment_method: "Virement bancaire", mark_paid: false });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSoldeSubmitting(false);
    }
  };

  if (loading) return <p className="text-white/55 italic">Chargement…</p>;

  const isDevis = form.doc_type === "devis";
  const statusList = isDevis ? STATUS_DEVIS : STATUS_FACT;

  return (
    <div data-testid="admin-devis-editor" className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <Link to="/admin/devis" data-testid="admin-devis-back" className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] text-white/55 hover:text-[#DCB457]">
            <ArrowLeft size={12} strokeWidth={1.4} /> Tous les documents
          </Link>
          {rental?.id && <Link to={`/admin/location/dossiers/${rental.id}`} data-testid="admin-devis-rental-link" className="ml-5 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] text-[#DCB457] hover:underline">
            <Package size={12} strokeWidth={1.4} /> Location {rental.number}
          </Link>}
          <h1 className="font-serif-luxe text-4xl md:text-5xl mt-3">
            {isNew ? "Nouveau" : "Éditer"} {isDevis ? "devis" : "facture"}
            {savedDoc?.number && <span className="italic gold-text"> · {savedDoc.number}</span>}
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={openPreview} disabled={previewLoading} data-testid="admin-devis-preview" className="btn-ghost-gold inline-flex items-center gap-2 disabled:opacity-60">
            {previewLoading ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} strokeWidth={1.4} />}
            Aperçu PDF
          </button>
          {savedDoc && (
            <button onClick={openPdf} data-testid="admin-devis-pdf" className="btn-ghost-gold inline-flex items-center gap-2">
              <Eye size={14} strokeWidth={1.4} /> PDF final
            </button>
          )}
          {savedDoc && savedDoc.client_email && (
            <button onClick={sendDoc} data-testid="admin-devis-send" className="btn-ghost-gold inline-flex items-center gap-2">
              <Send size={14} strokeWidth={1.4} /> Envoyer
            </button>
          )}
          {savedDoc?.doc_type === "devis" && savedDoc?.acompte_invoice_id && !savedDoc?.balance_invoice_id && (
            <button
              onClick={() => setSoldeModalOpen(true)}
              data-testid="admin-devis-generate-solde"
              className="btn-ghost-gold inline-flex items-center gap-2 border-emerald-400/40 text-emerald-300 hover:bg-emerald-400/10"
              title="Générer manuellement la facture de solde"
            >
              <Receipt size={14} strokeWidth={1.4} /> Générer facture de solde
            </button>
          )}
          {savedDoc?.balance_invoice_number && (
            <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-emerald-300/85 border border-emerald-400/30 px-2 py-1.5" title={`Facture de solde : ${savedDoc.balance_invoice_number}`}>
              <Receipt size={11} strokeWidth={1.5} /> Solde: {savedDoc.balance_invoice_number}
            </span>
          )}
          <button onClick={save} disabled={saving} data-testid="admin-devis-save" className="btn-gold inline-flex items-center gap-2 disabled:opacity-60">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} strokeWidth={1.4} />}
            Enregistrer
          </button>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-5">
          {/* Type de doc */}
          {isNew && (
            <div className="card-luxe p-4 flex items-center gap-3">
              <span className="text-[11px] uppercase tracking-[0.2em] text-white/60">Type :</span>
              <select className="input-luxe w-auto" value={form.doc_type} onChange={(e) => update("doc_type", e.target.value)} data-testid="admin-devis-type">
                <option value="devis">Devis</option>
                <option value="facture">Facture</option>
              </select>
            </div>
          )}

          {/* Client */}
          <div className="card-luxe p-5 md:p-6 space-y-3">
            <p className="eyebrow">Client</p>

            {/* Rattacher un client existant */}
            <div className="relative">
              <div className="flex items-center gap-2 border border-[#DCB457]/25 bg-black/60 px-3 py-2 focus-within:border-[#DCB457]">
                <Search size={13} strokeWidth={1.5} className="text-[#DCB457]/70 shrink-0" />
                <input
                  type="text"
                  value={pickerQuery}
                  onChange={(e) => { setPickerQuery(e.target.value); setPickerOpen(true); }}
                  onFocus={() => setPickerOpen(true)}
                  onBlur={() => setTimeout(() => setPickerOpen(false), 180)}
                  placeholder="Rattacher un client existant (nom, email, ville)…"
                  className="bg-transparent text-sm outline-none flex-1 placeholder:text-white/40"
                  data-testid="admin-devis-client-picker"
                />
                {(form.client_name || form.client_email) && (
                  <button
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, client_name: "", client_email: "", client_phone: "", client_address: "", ...EMPTY_PROFILE }))}
                    title="Vider les champs client"
                    className="text-white/45 hover:text-white/80"
                    data-testid="admin-devis-client-clear"
                  >
                    <X size={13} strokeWidth={1.5} />
                  </button>
                )}
              </div>
              {pickerOpen && filteredClients.length > 0 && (
                <ul
                  data-testid="admin-devis-client-picker-list"
                  className="absolute z-30 left-0 right-0 top-full mt-1 max-h-72 overflow-auto bg-[#0a0a0a] border border-[#DCB457]/30 shadow-lg"
                >
                  {filteredClients.map((u) => (
                    <li key={u.id}>
                      <button
                        type="button"
                        onMouseDown={(e) => { e.preventDefault(); fillFromClient(u); }}
                        data-testid={`admin-devis-client-option-${u.id}`}
                        className="w-full text-left px-3 py-2 hover:bg-[#DCB457]/10 border-b border-white/5 last:border-b-0"
                      >
                        <div className="text-sm text-white">{u.name || `${u.prenom || ""} ${u.nom || ""}`.trim() || u.email}{u.raison_sociale && <span className="text-[#DCB457] ml-2 text-xs">{u.raison_sociale}</span>}</div>
                        <div className="text-[11px] text-white/50 mt-0.5">
                          {u.email}{u.phone ? ` · ${u.phone}` : ""}{u.ville ? ` · ${u.ville}` : ""}
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {pickerOpen && pickerQuery && filteredClients.length === 0 && (
                <div className="absolute z-30 left-0 right-0 top-full mt-1 bg-[#0a0a0a] border border-[#DCB457]/30 px-3 py-3 text-xs text-white/55 italic">
                  Aucun client trouvé — saisissez les coordonnées manuellement ci-dessous.
                </div>
              )}
            </div>

            <input className="input-luxe" placeholder="Nom complet *" required value={form.client_name} onChange={(e) => update("client_name", e.target.value)} data-testid="admin-devis-client-name" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input
                className="input-luxe"
                placeholder="Email"
                type="email"
                value={form.client_email}
                onChange={(e) => update("client_email", lowerEmail(e.target.value))}
                onBlur={async (e) => {
                  const email = lowerEmail(e.target.value.trim());
                  if (!email || !email.includes("@")) return;
                  try {
                    const { data } = await api.get("/admin/users/lookup", { params: { email } });
                    // Only fill empty fields — never overwrite what the admin typed.
                    setForm((f) => {
                      const patch = {};
                      if (!f.client_name && data.name) patch.client_name = data.name;
                      if (!f.client_phone && data.phone) patch.client_phone = data.phone;
                      const parts = [data.adresse, [data.code_postal, data.ville].filter(Boolean).join(" ")].filter(Boolean);
                      if (!f.client_address && parts.length) patch.client_address = parts.join(", ");
                      PROFILE_KEYS.forEach((k) => { if (!f[k] && data[k]) patch[k] = data[k]; });
                      return Object.keys(patch).length ? { ...f, ...patch } : f;
                    });
                    if (data.name || data.adresse) toast.success("Coordonnées client pré-remplies.");
                  } catch {
                    /* silent — user may not exist in the client area yet */
                  }
                }}
                data-testid="admin-devis-client-email"
              />
              <input className="input-luxe" placeholder="Téléphone" value={form.client_phone} onChange={(e) => update("client_phone", e.target.value)} />
            </div>
            <input className="input-luxe" placeholder="Adresse postale (auto-remplie si le client a un compte)" value={form.client_address} onChange={(e) => update("client_address", e.target.value)} />
            <ClientProfileFields form={form} update={update} />
          </div>

          {/* Événement */}
          <div className="card-luxe p-5 md:p-6 space-y-3">
            <p className="eyebrow">Événement</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <select className="input-luxe" value={form.event_type} onChange={(e) => update("event_type", e.target.value)} data-testid="admin-devis-event-type">
                {EVENT_TYPES.map((t) => <option key={t}>{t}</option>)}
              </select>
              <DatePickerField value={form.event_date} onChange={(v) => update("event_date", v)} placeholder="Date de l'événement (JJ/MM/AAAA)" clearable testid="admin-devis-event-date" />
              <input className="input-luxe" placeholder="Horaires (ex 19h - 04h)" value={form.event_time ?? ""} onChange={(e) => update("event_time", e.target.value)} data-testid="admin-devis-event-time" />
            </div>
            {rental?.start && <RentalPeriodNote rental={rental} />}
            <input className="input-luxe" placeholder="Lieu de réception (salle / adresse complète)" value={form.event_location} onChange={(e) => update("event_location", e.target.value)} data-testid="admin-devis-event-location" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <select className="input-luxe" value={form.payment_method ?? "Virement bancaire"} onChange={(e) => update("payment_method", e.target.value)} data-testid="admin-devis-payment-method">
                <option>Virement bancaire</option>
                <option>Chèque</option>
                <option>Espèces</option>
                <option>Carte bancaire</option>
              </select>
            </div>
          </div>

          {/* Lignes */}
          <div className="card-luxe p-5 md:p-6">
            <div className="flex items-center justify-between mb-4">
              <p className="eyebrow">Lignes de prestation</p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setShowCatalog(true)}
                  data-testid="admin-devis-add-from-catalog"
                  className="text-xs inline-flex items-center gap-1.5 text-black bg-[#DCB457] px-2.5 py-1 hover:bg-[#E5C24A]"
                >
                  <Package size={11} strokeWidth={1.5} /> Depuis catalogue
                </button>
                <button
                  type="button"
                  onClick={addLine}
                  data-testid="admin-devis-add-line"
                  className="text-xs inline-flex items-center gap-1.5 text-[#DCB457] border border-[#DCB457]/40 px-2.5 py-1 hover:bg-[#DCB457]/10"
                >
                  <Plus size={11} strokeWidth={1.4} /> Ligne vide
                </button>
              </div>
            </div>
            <div className="space-y-3">
              {form.lines.map((l, i) => (
                <div key={i} className="border border-white/8 p-3" data-testid={`admin-devis-line-${i}`}>
                  <div className="grid grid-cols-12 gap-2 items-start">
                    <div className="col-span-12 md:col-span-6">
                      <textarea rows={2} className="input-luxe text-sm resize-none" placeholder="Description *" value={l.description} onChange={(e) => updateLine(i, "description", e.target.value)} />
                    </div>
                    <div className="col-span-3 md:col-span-2 relative">
                      <input type="number" step={l.unit === "heure" ? "1" : "0.01"} min={l.min_quantity || 0} className={`input-luxe text-sm ${l.unit === "heure" ? "pr-8" : ""}`} placeholder={l.unit === "heure" ? "Heures" : "Qté"} value={l.quantity} onChange={(e) => updateLine(i, "quantity", e.target.value)} data-testid={`admin-devis-line-qty-${i}`} />
                      {l.unit === "heure" && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-white/45 pointer-events-none">h</span>}
                    </div>
                    <div className="col-span-5 md:col-span-2">
                      <input type="number" step="0.01" min="0" className="input-luxe text-sm" placeholder="Prix unitaire" value={l.unit_price} onChange={(e) => updateLine(i, "unit_price", e.target.value)} />
                    </div>
                    <div className="col-span-3 md:col-span-2 flex items-center justify-end gap-2">
                      <span className="text-sm text-[#DCB457] font-medium">{fmtEur(Number(l.quantity || 0) * Number(l.unit_price || 0))}</span>
                      <button onClick={() => removeLine(i)} className="text-red-300 hover:bg-red-500/10 p-1.5 border border-red-400/25">
                        <Trash2 size={11} strokeWidth={1.4} />
                      </button>
                    </div>
                  </div>
                  {l.unit === "heure" && (
                    <p className="mt-2 text-xs" data-testid={`admin-devis-line-hourly-${i}`}>
                      <span className="text-[#DCB457]">{Number(l.quantity || 0)} heure{Number(l.quantity) > 1 ? "s" : ""} × {fmtEur(l.unit_price)} / heure = {fmtEur(Number(l.quantity || 0) * Number(l.unit_price || 0))}</span>
                      {l.min_quantity && Number(l.quantity) < Number(l.min_quantity) && (
                        <span className="ml-3 text-red-300" data-testid={`admin-devis-line-min-warning-${i}`}>Minimum {l.min_quantity} heures</span>
                      )}
                    </p>
                  )}
                  {l.is_admin_only && (
                    <LineMarginCalculator line={l} onUpdate={(patch) => setForm((f) => { const lines = [...f.lines]; lines[i] = { ...lines[i], ...patch }; return { ...f, lines }; })} />
                  )}
                </div>
              ))}
            </div>

            <div className="mt-5 pt-5 border-t border-[#DCB457]/15 space-y-3">
              {/* Sous-total */}
              <div className="flex justify-between items-center text-sm">
                <span className="text-white/65">Sous-total TTC</span>
                <span className="text-white/85 font-medium">{fmtEur(subtotal)}</span>
              </div>

              {/* Remise commerciale */}
              <div className="grid grid-cols-12 gap-2 items-center">
                <label className="col-span-4 text-xs text-white/60">Remise commerciale</label>
                <select
                  className="col-span-3 input-luxe text-xs !py-1 !px-2"
                  value={form.discount_type || "none"}
                  onChange={(e) => update("discount_type", e.target.value)}
                  data-testid="admin-devis-discount-type"
                >
                  <option value="none">Aucune</option>
                  <option value="percent">Pourcentage (%)</option>
                  <option value="amount">Montant fixe (€)</option>
                </select>
                {(form.discount_type && form.discount_type !== "none") ? (
                  <div className="col-span-3 flex items-center gap-1">
                    <input
                      type="number" step="0.01" min="0"
                      max={form.discount_type === "percent" ? 100 : undefined}
                      className="input-luxe text-xs !py-1 !px-2 w-full"
                      value={form.discount_value}
                      onChange={(e) => update("discount_value", Number(e.target.value) || 0)}
                      data-testid="admin-devis-discount-value"
                    />
                    <span className="text-xs text-white/50">{form.discount_type === "percent" ? "%" : "€"}</span>
                  </div>
                ) : <div className="col-span-3" />}
                <span className="col-span-2 text-right text-xs text-red-300">
                  {discountAmount > 0 ? `− ${fmtEur(discountAmount)}` : ""}
                </span>
              </div>

              {/* Total TTC */}
              <div className="flex justify-between items-center pt-2 border-t border-[#DCB457]/10">
                <span className="text-white/65 text-sm">Total TTC</span>
                <span className="font-serif-luxe text-2xl gold-text" data-testid="admin-devis-total-ttc">{fmtEur(totalTtc)}</span>
              </div>

              {/* Acompte configurable */}
              {isDevis && (
                <div className="grid grid-cols-12 gap-2 items-center pt-2 border-t border-[#DCB457]/10">
                  <label className="col-span-4 text-xs text-white/60">Acompte</label>
                  <select
                    className="col-span-3 input-luxe text-xs !py-1 !px-2"
                    value={form.deposit_type || "percent"}
                    onChange={(e) => update("deposit_type", e.target.value)}
                    data-testid="admin-devis-deposit-type"
                  >
                    <option value="percent">Pourcentage (%)</option>
                    <option value="amount">Montant fixe (€)</option>
                  </select>
                  {form.deposit_type === "amount" ? (
                    <div className="col-span-3 flex items-center gap-1">
                      <input
                        type="number" step="0.01" min="0"
                        className="input-luxe text-xs !py-1 !px-2 w-full"
                        value={form.deposit_amount}
                        onChange={(e) => update("deposit_amount", Number(e.target.value) || 0)}
                        data-testid="admin-devis-deposit-amount"
                      />
                      <span className="text-xs text-white/50">€</span>
                    </div>
                  ) : (
                    <div className="col-span-3 flex items-center gap-1">
                      <input
                        type="number" step="1" min="0" max="100"
                        className="input-luxe text-xs !py-1 !px-2 w-full"
                        value={form.deposit_pct}
                        onChange={(e) => update("deposit_pct", Number(e.target.value) || 0)}
                        data-testid="admin-devis-deposit-pct"
                      />
                      <span className="text-xs text-white/50">%</span>
                    </div>
                  )}
                  <span className="col-span-2 text-right text-sm text-white/85" data-testid="admin-devis-deposit-amount-calc">{fmtEur(deposit)}</span>
                </div>
              )}
              {isDevis && (
                <div className="flex justify-between items-center text-xs text-white/50">
                  <span>Solde à régler</span>
                  <span data-testid="admin-devis-balance">{fmtEur(Math.max(0, totalTtc - deposit))}</span>
                </div>
              )}
            </div>
          </div>

          {/* Conditions / notes */}
          <div className="card-luxe p-5 md:p-6 space-y-3">
            <p className="eyebrow">Conditions (imprimées sur le PDF)</p>
            <textarea rows={4} className="input-luxe resize-none text-sm" value={form.terms} onChange={(e) => update("terms", e.target.value)} placeholder="Acompte 30% à la signature…" />
          </div>
        </div>

        {/* Right column */}
        <aside className="space-y-5">
          {!isNew && form.status && (
            <div className="card-luxe p-5">
              <p className="eyebrow">Statut</p>
              <select className="input-luxe mt-3" value={form.status} onChange={(e) => update("status", e.target.value)} data-testid="admin-devis-status">
                {statusList.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          )}

          <div className="card-luxe p-5 space-y-3">
            <p className="eyebrow">Contrat de prestation</p>
            <select className="input-luxe" value={form.contract_template ?? ""} onChange={(e) => update("contract_template", e.target.value)} data-testid="admin-devis-contract-tpl">
              {CONTRACT_TEMPLATES.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
            </select>
            <p className="text-[11px] text-white/45 leading-snug">
              Si sélectionné, le contrat sera annexé automatiquement au PDF du devis (articles + signature + RIB).
            </p>
          </div>

          {savedDoc && savedDoc.doc_type === "devis" && (
            <div className="card-luxe p-5 space-y-3" data-testid="admin-devis-signature-panel">
              <p className="eyebrow">Signature en ligne</p>
              {savedDoc.is_signed ? (
                <div className="text-xs space-y-1.5">
                  <p className="text-emerald-300 inline-flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                    Signé le {new Date(savedDoc.signed_at).toLocaleString("fr-FR")}
                  </p>
                  <p className="text-white/55">par <span className="text-white/85">{savedDoc.signature_name}</span></p>
                  {savedDoc.signed_ip && <p className="text-white/40 font-mono text-[10px]">IP {savedDoc.signed_ip}</p>}
                </div>
              ) : (
                <>
                  <p className="text-[11px] text-white/55 leading-snug">
                    Lien de signature à transmettre au client. Inclus automatiquement dans l'email d'envoi.
                  </p>
                  <button
                    onClick={() => {
                      const url = `${window.location.origin}/signature/${savedDoc.signature_token}`;
                      navigator.clipboard.writeText(url);
                      toast.success("Lien de signature copié.");
                    }}
                    data-testid="admin-devis-copy-sign-link"
                    className="btn-ghost-gold inline-flex items-center gap-2 w-full justify-center text-xs"
                  >
                    Copier le lien de signature
                  </button>
                </>
              )}
            </div>
          )}

          <div className="card-luxe p-5 space-y-3">
            <p className="eyebrow">Dates limites</p>
            {isDevis ? (
              <div className="block">
                <span className="text-[11px] text-white/55 mb-1 block">Devis valable jusqu'au</span>
                <DatePickerField value={form.valid_until} onChange={(v) => update("valid_until", v)} clearable testid="admin-devis-valid-until" />
              </div>
            ) : (
              <div className="block">
                <span className="text-[11px] text-white/55 mb-1 block">Paiement attendu pour le</span>
                <DatePickerField value={form.payment_due} onChange={(v) => update("payment_due", v)} clearable testid="admin-devis-payment-due" />
              </div>
            )}
          </div>

          <div className="card-luxe p-5">
            <p className="eyebrow">Notes internes (non imprimées)</p>
            <textarea rows={4} className="input-luxe resize-none text-xs mt-3" value={form.notes} onChange={(e) => update("notes", e.target.value)} placeholder="Notes pour vous-même…" />
          </div>
        </aside>
      </div>

      {/* Catalog picker modal */}
      {showCatalog && (
        <CatalogPickerModal
          catalog={catalog}
          setCatalog={setCatalog}
          query={catalogQuery}
          setQuery={setCatalogQuery}
          onPick={(pkg) => { addLineFromCatalog(pkg); }}
          onClose={() => setShowCatalog(false)}
        />
      )}

      {/* PDF Preview modal — live PDF without persisting the doc */}
      {previewOpen && (
        <div
          data-testid="admin-devis-preview-modal"
          className="fixed inset-0 z-[95] flex items-center justify-center p-4"
          onClick={closePreview}
        >
          <div className="absolute inset-0 bg-black/85 backdrop-blur-sm" />
          <div
            className="relative w-full max-w-6xl h-[92vh] bg-[#0a0a0a] border border-[#DCB457]/30 shadow-2xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-3 p-4 border-b border-[#DCB457]/15">
              <div>
                <p className="eyebrow">Aperçu PDF</p>
                <h3 className="font-serif-luxe text-lg mt-0.5">
                  Contrôle visuel avant envoi
                  <span className="ml-2 text-[10px] uppercase tracking-[0.22em] text-white/45 italic">(non enregistré)</span>
                </h3>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={openPreview}
                  disabled={previewLoading}
                  data-testid="admin-devis-preview-refresh"
                  className="btn-ghost-gold inline-flex items-center gap-2 text-xs disabled:opacity-60"
                >
                  {previewLoading ? <Loader2 size={12} className="animate-spin" /> : <Eye size={12} strokeWidth={1.4} />}
                  Actualiser
                </button>
                {previewUrl && (
                  <a
                    href={previewUrl}
                    download={`apercu-${form.client_name?.replace(/\s+/g, "-") || "devis"}.pdf`}
                    data-testid="admin-devis-preview-download"
                    className="btn-ghost-gold inline-flex items-center gap-2 text-xs"
                  >
                    Télécharger
                  </a>
                )}
                <button
                  onClick={closePreview}
                  data-testid="admin-devis-preview-close"
                  aria-label="Fermer l'aperçu"
                  className="text-white/50 hover:text-[#DCB457] p-1"
                >
                  <X size={18} strokeWidth={1.4} />
                </button>
              </div>
            </div>
            <div className="flex-1 min-h-0 bg-[#1a1a1a]">
              {previewLoading && !previewUrl ? (
                <div className="w-full h-full flex flex-col items-center justify-center gap-3 text-white/55">
                  <Loader2 size={28} className="animate-spin text-[#DCB457]" />
                  <p className="text-sm italic">Génération de l'aperçu…</p>
                </div>
              ) : previewUrl ? (
                <iframe
                  key={previewUrl}
                  src={previewUrl}
                  title="Aperçu PDF"
                  data-testid="admin-devis-preview-iframe"
                  className="w-full h-full border-0 bg-white"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white/45 text-sm italic">
                  Aucun aperçu disponible.
                </div>
              )}
            </div>
            <div className="p-3 border-t border-[#DCB457]/10 text-[11px] text-white/45 text-center">
              Ce PDF est un aperçu en direct — il n'est pas enregistré tant que vous n'avez pas cliqué sur « Enregistrer ».
              Toute modification dans le formulaire nécessite un clic sur <span className="text-[#DCB457]">Actualiser</span> pour être visible.
            </div>
          </div>
        </div>
      )}

      {/* Générer facture de solde manuellement */}
      {soldeModalOpen && (
        <div
          data-testid="admin-devis-solde-modal"
          className="fixed inset-0 z-[95] flex items-center justify-center p-4"
          onClick={() => setSoldeModalOpen(false)}
        >
          <div className="absolute inset-0 bg-black/85 backdrop-blur-sm" />
          <div
            className="relative w-full max-w-md bg-[#0a0a0a] border border-[#DCB457]/30 shadow-2xl p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="eyebrow">Facture de solde</p>
                <h3 className="font-serif-luxe text-xl mt-1">Générer manuellement</h3>
              </div>
              <button
                onClick={() => setSoldeModalOpen(false)}
                data-testid="admin-devis-solde-close"
                aria-label="Fermer"
                className="text-white/45 hover:text-[#DCB457] p-1"
              >
                <X size={18} strokeWidth={1.4} />
              </button>
            </div>

            <div className="mb-4 p-3 bg-[#DCB457]/5 border border-[#DCB457]/15 text-xs text-white/70 leading-relaxed">
              <b className="text-[#DCB457]">Devis {savedDoc?.number}</b> — {savedDoc?.client_name}<br />
              Total TTC : <b>{fmtEur(savedDoc?.total_ttc || 0)}</b><br />
              Acompte : <b>{fmtEur(savedDoc?.deposit_ttc || 0)}</b> {savedDoc?.acompte_invoice_number && <span className="text-white/40">({savedDoc.acompte_invoice_number})</span>}<br />
              Solde à générer : <b className="gold-text">{fmtEur(Math.max(0, (savedDoc?.total_ttc || 0) - (savedDoc?.deposit_ttc || 0)))}</b>
            </div>

            <div className="space-y-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={soldeForm.mark_paid}
                  onChange={(e) => setSoldeForm((s) => ({ ...s, mark_paid: e.target.checked }))}
                  data-testid="admin-devis-solde-markpaid"
                  className="mt-1 accent-[#DCB457]"
                />
                <span className="text-sm text-white/85">
                  Le solde a déjà été payé — marquer la facture comme <b className="gold-text">payée</b> immédiatement
                </span>
              </label>

              {soldeForm.mark_paid && (
                <>
                  <div>
                    <label className="block text-xs uppercase tracking-[0.22em] text-white/55 mb-1.5">Date de paiement</label>
                    <DatePickerField value={soldeForm.paid_at} onChange={(v) => setSoldeForm((s) => ({ ...s, paid_at: v }))} testid="admin-devis-solde-paidat" />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-[0.22em] text-white/55 mb-1.5">Moyen de paiement</label>
                    <select
                      value={soldeForm.payment_method}
                      onChange={(e) => setSoldeForm((s) => ({ ...s, payment_method: e.target.value }))}
                      data-testid="admin-devis-solde-method"
                      className="input-luxe w-full"
                    >
                      <option value="Virement bancaire">Virement bancaire</option>
                      <option value="Chèque">Chèque</option>
                      <option value="Espèces">Espèces</option>
                      <option value="Carte bancaire (Stripe)">Carte bancaire (Stripe)</option>
                      <option value="Autre">Autre</option>
                    </select>
                  </div>
                </>
              )}
              {!soldeForm.mark_paid && (
                <p className="text-xs text-white/50 italic">
                  La facture sera envoyée au client par email pour paiement (statut « envoyée »).
                </p>
              )}
            </div>

            <div className="mt-6 flex items-center justify-end gap-2">
              <button
                onClick={() => setSoldeModalOpen(false)}
                className="text-xs uppercase tracking-[0.22em] text-white/60 hover:text-white px-3 py-2"
              >
                Annuler
              </button>
              <button
                onClick={submitGenerateSolde}
                disabled={soldeSubmitting}
                data-testid="admin-devis-solde-submit"
                className="btn-gold inline-flex items-center gap-2 disabled:opacity-60"
              >
                {soldeSubmitting ? <Loader2 size={14} className="animate-spin" /> : <Receipt size={14} strokeWidth={1.4} />}
                Générer la facture
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ==========================================================================
// Catalog picker modal — searchable list grouped by category
// ==========================================================================
const CAT_LABELS = {
  mariage: "Mariage",
  anniversaire: "Anniversaire",
  bapteme: "Baptême / Communion",
  senior: "Résidence senior",
  association: "Association",
  chanteur: "Chanteur live",
  blindtest: "Blind test / Karaoké",
  photobooth: "Photobooth",
  "photo-video": "Photo & Vidéo",
  musiciens: "Musiciens live",
  "livre-or": "Livre d'Or",
  entreprise: "Entreprise",
  "bar-restaurant": "Bars & Restaurants",
  mairie: "Mairie / Collectivité",
  option: "Options / Add-ons",
  autre: "Autre (admin)",
};

function LineMarginCalculator({ line, onUpdate }) {
  const cost = Number(line.prestataire_cost || 0);
  const urssaf = Math.max(0, Math.min(0.5, Number(line.urssaf_rate ?? 0.22)));
  const target = Number(line.target_net_margin || 0);
  const denom = 1 - urssaf;
  const computed = denom > 0 && (cost > 0 || target > 0) ? (cost + target) / denom : 0;
  const rounded = Math.ceil(computed / 10) * 10;
  const urssafDue = rounded * urssaf;
  const netAfterUrssaf = rounded - urssafDue;
  const realMargin = netAfterUrssaf - cost;
  const canApply = rounded > 0 && rounded !== Number(line.unit_price);
  return (
    <div className="mt-3 pt-3 border-t border-purple-400/25 bg-purple-500/[0.04] -mx-3 -mb-3 px-3 pb-3 rounded-b-sm" data-testid="line-margin-calc">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <p className="text-[10px] uppercase tracking-[0.18em] text-purple-300 font-medium">Calculateur de marge nette (micro-BNC)</p>
        <span
          className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.16em] text-white/70 border border-white/30 bg-black/50 px-2 py-0.5"
          title="Ces informations restent en base — le PDF envoyé au client n'affiche que description, quantité et prix unitaire."
          data-testid="line-internal-badge"
        >
          <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/></svg>
          Info interne · invisible client
        </span>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <label className="block">
          <span className="text-[9px] uppercase tracking-[0.14em] text-white/55">Coût prestataire (€)</span>
          <input className="input-luxe mt-1 !text-xs !py-1.5" type="number" step="1" min="0" value={line.prestataire_cost ?? ""} onChange={(e) => onUpdate({ prestataire_cost: e.target.value === "" ? null : Number(e.target.value) })} placeholder="500" data-testid="line-prestataire-cost" />
        </label>
        <label className="block">
          <span className="text-[9px] uppercase tracking-[0.14em] text-white/55">URSSAF (%)</span>
          <input className="input-luxe mt-1 !text-xs !py-1.5" type="number" step="0.1" min="0" max="50" value={line.urssaf_rate != null ? (line.urssaf_rate * 100).toFixed(1) : ""} onChange={(e) => onUpdate({ urssaf_rate: e.target.value === "" ? null : Number(e.target.value) / 100 })} placeholder="22.0" data-testid="line-urssaf-rate" />
        </label>
        <label className="block">
          <span className="text-[9px] uppercase tracking-[0.14em] text-white/55">Marge nette voulue (€)</span>
          <input className="input-luxe mt-1 !text-xs !py-1.5" type="number" step="10" min="0" value={line.target_net_margin ?? ""} onChange={(e) => onUpdate({ target_net_margin: e.target.value === "" ? null : Number(e.target.value) })} placeholder="100" data-testid="line-target-margin" />
        </label>
      </div>
      {computed > 0 && (
        <>
          <div className="mt-2 p-2 bg-black/40 border border-purple-400/20 grid grid-cols-2 gap-x-4 gap-y-1 text-[11px]">
            <span className="text-white/60">Prix client calculé :</span>
            <span className="text-right font-mono">{fmtEur(computed)}</span>
            <span className="text-white/60">Arrondi (10 € sup.) :</span>
            <span className="text-right font-mono text-purple-300 font-semibold" data-testid="line-rounded-price">{fmtEur(rounded)}</span>
            <span className="text-white/45">− URSSAF ({(urssaf * 100).toFixed(1)}%) :</span>
            <span className="text-right font-mono text-red-300">− {fmtEur(urssafDue)}</span>
            <span className="text-white/45">− Coût prestataire :</span>
            <span className="text-right font-mono text-red-300">− {fmtEur(cost)}</span>
            <span className="text-emerald-300 font-semibold">Marge nette réelle :</span>
            <span className="text-right font-mono text-emerald-300 font-semibold" data-testid="line-real-margin">{fmtEur(realMargin)}</span>
          </div>
          {canApply && (
            <button type="button" onClick={() => onUpdate({ unit_price: rounded })} data-testid="line-apply-price" className="mt-2 w-full inline-flex items-center justify-center gap-2 px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] text-purple-200 hover:text-white border border-purple-400/50 hover:border-purple-400 hover:bg-purple-500/10 transition">
              Appliquer {fmtEur(rounded)} au prix unitaire
            </button>
          )}
        </>
      )}
    </div>
  );
}

function ClientProfileFields({ form, update }) {
  const types = [["particulier", "Particulier"], ["professionnel", "Professionnel"], ["association", "Association"]];
  const fields = [["raison_sociale", "Raison sociale"], ["nom_commercial", "Nom commercial"], ["siret", "SIRET"], ["tva_number", "N° TVA intracom"], ["contact_prenom", "Prénom contact"], ["contact_nom", "Nom contact"], ["contact_fonction", "Fonction contact"], ["email_facturation", "Email de facturation"]];
  return (
    <div className="space-y-3" data-testid="admin-devis-client-profile">
      <div className="flex gap-2 flex-wrap">
        {types.map(([v, l]) => (
          <button key={v} type="button" onClick={() => update("client_type", v)} data-testid={`admin-devis-client-type-${v}`}
            className={`px-3 py-1 text-[11px] border transition-colors ${(form.client_type || "particulier") === v ? "bg-[#DCB457] text-black border-[#DCB457]" : "border-[#DCB457]/30 text-white/65 hover:border-[#DCB457]/60"}`}>{l}</button>
        ))}
      </div>
      {isPro(form) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {fields.map(([k, l]) => (
            <input key={k} className="input-luxe" placeholder={l} value={form[k] || ""} onChange={(e) => update(k, e.target.value)} data-testid={`admin-devis-client-${k}`} />
          ))}
        </div>
      )}
    </div>
  );
}

function CatalogPickerModal({ catalog, setCatalog, query, setQuery, onPick, onClose }) {
  useEffect(() => {
    if (catalog) return;
    // Use the ADMIN catalog endpoint so all active packages appear — including
    // admin_only forfaits (Mise en relation prestataire) that are hidden from public.
    catalogApi.list()
      .then((data) => setCatalog(Array.isArray(data) ? data.filter((p) => p.is_active !== false) : []))
      .catch(() => setCatalog([]));
  }, [catalog, setCatalog]);

  const items = catalog || [];
  const q = query.trim().toLowerCase();
  const filtered = q
    ? items.filter((p) => (p.name || "").toLowerCase().includes(q) ||
                           (p.subtitle || "").toLowerCase().includes(q) ||
                           (CAT_LABELS[p.category] || "").toLowerCase().includes(q))
    : items;

  const grouped = filtered.reduce((acc, p) => {
    const c = p.category || "autre";
    (acc[c] = acc[c] || []).push(p);
    return acc;
  }, {});

  const CATEGORY_PREFERRED_ORDER = ["mariage", "anniversaire", "bapteme", "senior", "association",
                                    "chanteur", "blindtest", "photobooth", "photo-video", "musiciens",
                                    "livre-or", "entreprise", "bar-restaurant", "mairie", "option", "autre"];
  // Defensive: render preferred order first, THEN any other category present in the grouped result
  // (avoids silently hiding new categories added later — the exact bug that caused this iteration).
  const knownGrouped = CATEGORY_PREFERRED_ORDER.filter((c) => grouped[c]);
  const extraGrouped = Object.keys(grouped).filter((c) => !CATEGORY_PREFERRED_ORDER.includes(c));
  const categoryOrder = [...knownGrouped, ...extraGrouped];

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4" onClick={onClose} data-testid="admin-catalog-modal">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
      <div className="relative w-full max-w-3xl max-h-[85vh] bg-[#0a0a0a] border border-[#DCB457]/30 shadow-2xl flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 p-5 border-b border-[#DCB457]/15">
          <div>
            <p className="eyebrow">Catalogue produits</p>
            <h3 className="font-serif-luxe text-xl mt-1">Ajouter au devis</h3>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-[#DCB457]" data-testid="admin-catalog-close" aria-label="Fermer">
            <X size={18} strokeWidth={1.4} />
          </button>
        </div>

        <div className="p-4 border-b border-[#DCB457]/10">
          <div className="relative">
            <Search size={14} strokeWidth={1.4} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="admin-catalog-search"
              placeholder="Rechercher (mariage, photobooth, karaoké…)"
              className="w-full pl-9 pr-3 py-2.5 bg-black/50 border border-white/10 text-sm text-white focus:border-[#DCB457] outline-none"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {catalog === null ? (
            <p className="text-white/50 flex items-center gap-2 justify-center py-8"><Loader2 size={14} className="animate-spin" /> Chargement du catalogue…</p>
          ) : filtered.length === 0 ? (
            <p className="text-white/50 text-center py-8 italic">Aucun résultat.</p>
          ) : (
            <div className="space-y-6">
              {categoryOrder.filter((c) => grouped[c]).map((cat) => (
                <div key={cat}>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#DCB457] mb-2">{CAT_LABELS[cat] || cat}</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {grouped[cat].map((p) => (
                      <button
                        key={p.id || p.name}
                        onClick={() => { onPick(p); onClose(); }}
                        data-testid={`admin-catalog-item-${p.id || p.name}`}
                        className="text-left p-3 border border-white/10 hover:border-[#DCB457]/50 hover:bg-[#DCB457]/5 transition group"
                      >
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <p className="text-sm text-white/90 font-medium group-hover:text-[#DCB457]">{p.name}</p>
                          <p className="text-sm text-[#DCB457] font-bold whitespace-nowrap">{p.price_label || (p.price_ttc ? `${p.price_ttc} €` : "—")}</p>
                        </div>
                        {p.subtitle && <p className="text-[11px] text-white/50 leading-snug">{p.subtitle}</p>}
                        {p.unit === "heure" ? <p className="text-[10px] text-white/40 mt-1">Tarif horaire · minimum {p.min_quantity || 1} h</p> : p.duration_hours ? <p className="text-[10px] text-white/40 mt-1">{p.duration_hours} h de prestation</p> : null}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="p-4 border-t border-[#DCB457]/10 text-[11px] text-white/45 text-center">
          Cliquez sur un élément pour l'ajouter comme ligne au devis. Vous pourrez ensuite ajuster quantité et prix.
        </div>
      </div>
    </div>
  );
}
